/**
 * EmptyState Component
 * O vazio das listagens: diz de onde vêm os dados, quais pré-requisitos faltam
 * no escopo de quem está olhando, e qual é o próximo clique.
 *
 * Alvo visual: docs/superpowers/specs/2026-08-20-385-estados-vazios-mockup.html
 */

import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

import { CATALOGO_DE_TELAS } from '../../config/menuConfig';
import { PREREQ_CHAINS, PREREQ_NODES } from '../../config/prerequisites';
import { usePermissions } from '../../hooks/usePermissions';
import { usePrerequisites } from '../../hooks/usePrerequisites';
import { useAuth } from '../../stores/authStore';
import { TelaDoCatalogo } from '../../types/menuConfig';
import { resolveAffinity } from '../../utils/affinity';

export interface EmptyStateAction {
  label: string;
  /** id de uma tela do catálogo — dela saem rota e módulo da permissão de criar. */
  node: string;
  /** Texto exibido no lugar da ação quando falta a permissão de criar. */
  deniedHint: string;
}

export interface EmptyStateConfig {
  title: string;
  /** Responde "de onde vêm os dados desta lista". É o que separa ensinar de rotular. */
  description: string;
  /** Chave de uma cadeia no catálogo. Rótulos, rotas e permissões vêm de lá. */
  prereqChain?: string;
  action?: EmptyStateAction;
}

export interface EmptyStateProps {
  config: EmptyStateConfig;
  /** Filtro sem resultado é outro estado: texto do próprio componente e nenhuma cadeia. */
  filtersActive?: boolean;
  onClearFilters?: () => void;
  /** Card do mobile: descrição encurtada e rótulo curto dos elos. */
  compact?: boolean;
}

const FILTERED_TITLE = 'Nenhum resultado para os filtros aplicados';
const FILTERED_DESCRIPTION =
  'Existem registros nesta tela — nenhum deles atende aos filtros atuais.';

const PENDING_COUNT_WORDS: Record<number, string> = { 2: 'dois', 3: 'três', 4: 'quatro' };

function findMenuItem(node: string): TelaDoCatalogo | undefined {
  return CATALOGO_DE_TELAS.find((tela) => tela.id === node);
}

/** A permissão de criar sai da de ler, trocando o sufixo: `professional.read` → `.create`. */
function moduleOf(item: TelaDoCatalogo | undefined): string | undefined {
  return item?.permissao?.split('.')[0];
}

/** No card do mobile fica a primeira frase — é ela que diz de onde vêm os dados. */
function firstSentence(text: string): string {
  const match = text.match(/^[^.!?]*[.!?]/);
  return match ? match[0] : text;
}

export function EmptyState({
  config,
  filtersActive = false,
  onClearFilters,
  compact = false,
}: EmptyStateProps): React.ReactElement {
  const { user } = useAuth();
  const { canCreate } = usePermissions();
  const affinity = resolveAffinity(user);

  const chain = config.prereqChain ? PREREQ_CHAINS[config.prereqChain] ?? [] : [];
  const prerequisites = usePrerequisites(chain, !filtersActive && chain.length > 0);

  const containerSx = { py: compact ? 3 : 6, px: 2, textAlign: 'center' as const };

  if (filtersActive) {
    return (
      <Box sx={containerSx}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {FILTERED_TITLE}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {FILTERED_DESCRIPTION}
        </Typography>
        {onClearFilters && (
          <Button variant="outlined" size="small" onClick={onClearFilters} sx={{ mt: 2 }}>
            Limpar filtros
          </Button>
        )}
      </Box>
    );
  }

  // Elo ausente na resposta é elo que o usuário não pode ler: some da lista e da contagem.
  const steps =
    prerequisites.status === 'ready'
      ? chain
          .filter((node) => node in prerequisites.nodes && node in PREREQ_NODES)
          .map((node) => ({ node, done: prerequisites.nodes[node] }))
      : [];

  const firstPendingIndex = steps.findIndex((step) => !step.done);
  const pendingCount = steps.filter((step) => !step.done).length;

  // Com um único elo pendente o próprio item já diz o que falta; a contagem só ajuda no plural.
  const countPhrase =
    !compact && pendingCount >= 2
      ? `Faltam ${PENDING_COUNT_WORDS[pendingCount] ?? pendingCount} passos antes.`
      : '';

  const description = compact
    ? firstSentence(config.description)
    : [config.description, countPhrase].filter(Boolean).join(' ');

  const actionItem = config.action ? findMenuItem(config.action.node) : undefined;
  const actionModule = moduleOf(actionItem);
  const canCreateAction = Boolean(actionModule && canCreate(actionModule));

  // Com um elo pendente, quem resolve o vazio é a ação DESSE elo, não a da tela:
  // um acionável por vez, e é sempre o primeiro passo que falta.
  const pendingStep = firstPendingIndex >= 0 ? steps[firstPendingIndex] : undefined;
  const pendingItem = pendingStep ? findMenuItem(pendingStep.node) : undefined;
  const pendingModule = moduleOf(pendingItem);
  const canCreatePending = Boolean(pendingModule && canCreate(pendingModule));

  return (
    <Box sx={containerSx}>
      <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={600} gutterBottom>
        {config.title}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mx: 'auto', maxWidth: 460 }}>
        {description}
      </Typography>

      {steps.length > 0 && (
        <Stack
          component="ol"
          spacing={1}
          sx={{
            listStyle: 'none',
            display: 'inline-flex',
            textAlign: 'left',
            m: 0,
            mt: 2,
            p: 0,
          }}
        >
          {steps.map((step, index) => {
            const node = PREREQ_NODES[step.node];
            const isNext = index === firstPendingIndex;
            const label = step.done
              ? node.done
              : compact && node.short
                ? node.short
                : node.pending[affinity];

            return (
              <Box
                component="li"
                key={step.node}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  // O elo que ainda não é a vez fica esmaecido: um acionável por vez.
                  opacity: step.done || isNext ? 1 : 0.55,
                }}
              >
                {step.done ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: isNext ? 'primary.main' : 'transparent',
                      color: isNext ? 'primary.contrastText' : 'text.disabled',
                      border: isNext ? 0 : 1,
                      borderColor: 'divider',
                    }}
                  >
                    {index + 1}
                  </Box>
                )}

                <Typography
                  variant="body2"
                  color={step.done ? 'text.secondary' : 'text.primary'}
                  fontWeight={isNext ? 600 : 400}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      )}

      {pendingStep ? (
        <Box sx={{ mt: 2 }}>
          {canCreatePending && pendingItem ? (
            <Button component={RouterLink} to={pendingItem.caminho} variant="contained" size="small">
              {PREREQ_NODES[pendingStep.node].actionLabel}
            </Button>
          ) : (
            // Botão que leva a um 403 promete uma saída que não existe.
            <Typography variant="body2" color="text.secondary">
              {PREREQ_NODES[pendingStep.node].deniedHint}
            </Typography>
          )}
        </Box>
      ) : (
        config.action && (
          <Box sx={{ mt: 2 }}>
            {canCreateAction && actionItem ? (
              <Button component={RouterLink} to={actionItem.caminho} variant="contained" size="small">
                {config.action.label}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {config.action.deniedHint}
              </Typography>
            )}
          </Box>
        )
      )}
    </Box>
  );
}

export default EmptyState;
